import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewChild,
    AfterViewInit,
    TemplateRef
} from '@angular/core';

import { Observable } from 'rxjs';

import { InputValidationModel } from '../../models/input-validation.model';
import { getProvider, ControlValueAccessorModel } from '../../models/control-value-accessor.model';
import { AutocompleteComponent } from '../Autocomplete/autocomplete.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { AutocompleteCustomOption } from '../Autocomplete/autocomplete-custom-option.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ActionKeys } from '../../enums/keys.enum';


export interface AutocompleteChipsOptions {
    emptyMessage?: string;
    dropdown?: boolean;
    forceSelection?: boolean;
    delay?: number;
    maxLength?: number;
    minLength?: number;
    scrollHeight?: string; // formatted like '200px'
    suggestions?: any[];
    suggestionsFetcher?: (string) => Observable<any[]>;
    customOptions?: AutocompleteCustomOption[];
    clearOnBlur?: boolean;
}

const autocompleteChipsDefaultOptions: AutocompleteChipsOptions = {
    emptyMessage: null,
    dropdown: false,
    forceSelection: true,
    delay: 300,
    maxLength: null,
    minLength: 3,
    scrollHeight: '200px',
    suggestions: [],
    suggestionsFetcher: null,
    customOptions: [],
    clearOnBlur: false,
};

/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the autocomplete has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaInvalid*** - use this to indicate state if the autocomplete can fail in certain scenario
 * + ***ariaLabel*** - use this to provide an accessible label for the autocomplete. Don't use this if ariaLabelledBy is used
 * + ***ariaLabelledBy*** - use this to associate an external label with the autocomplete. Use id of the label as this value. This overwrites ariaLabel
 * + ***clearAllChipsTootlip*** - use this to provide a tooltip for the clear all chips button
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-autocomplete-chips',
    templateUrl: './autocomplete-chips.component.html',
    styleUrls: ['./autocomplete-chips.component.scss'],
    providers: [getProvider(AutocompleteChipsComponent)],
    standalone: false
})
export class AutocompleteChipsComponent<T> extends ControlValueAccessorModel<T[]> implements OnInit, AfterViewInit {

    @Input() id: string = null;
    @Input() disabled: boolean = false;
    @Input() header: string = null;
    @Input() placeholder: string = '';
    @Input() addOnKeys: ActionKeys[] = [ ActionKeys.TAB, ActionKeys.ENTER ];
    @Input() splitOnPattern: RegExp = null;
    @Input() trimQuotesOnNewChips: boolean = true;
    @Input() max: number = null;
    @Input() styleClass: string = null;
    @Input() ariaLabel: string;
    @Input() ariaLabelledBy: string;
    @Input() headerStyleClass: string;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() ariaDescribedBy: string = '';
    @Input() ariaInvalid: boolean = false;

    @Input() autocompleteOptions: AutocompleteChipsOptions;
    @Input() chipValidations: InputValidationModel[] = [];
    @Input() clearAllChipsTooltip: string = 'Core.button.clearAll';
    @Input() multiSelection: boolean = false;
    @Input() showHiddenOption: boolean = false;
    @Input() itemTemplate: TemplateRef<any> = null;

    // to pass through to autocomplete
    @Input() selectedItemsHideKey: string = null;

    @Input() field: keyof T = null;
    @Input() duplicateCheckField: keyof T = null;

    @Input() button: {
        tooltip?: string;
        text?: string;
        icon?: string;
    };
    @Input() required: boolean = false;

    @Output() buttonPress: EventEmitter<void> = new EventEmitter<void>();
    @Output() enterKeyUp: EventEmitter<string> = new EventEmitter();
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() allChipsRemoved: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('autocomplete') autocomplete: AutocompleteComponent;

    chipValidationErrors: string[] = [];
    searchString: string;

    readonly buttonTypes = ButtonTypesEnum;

    ngOnInit() {
        const options: AutocompleteChipsOptions = { ...autocompleteChipsDefaultOptions };
        Object.assign(options, this.autocompleteOptions);
        this.autocompleteOptions = options;
    }

    ngAfterViewInit(): void {
        this.autocomplete?.searchStringChanged.subscribe(searchString => {
            this.searchString = searchString;
        });
    }

    writeValue(value: T[]): void {
        this.value = value ?? [];
        this.onModelChange(this.value);
    }

    registerOnChange(fn: (value: T[]) => void): void {
        this.onModelChange = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    addChip(chip: T): void {
        this.chipValidationErrors = [];
        if (!this.isChipValid(chip) || this.isDuplicate(chip)) {
            return;
        }

        this.value.push(chip);

        if (this.autocomplete){
            this.autocomplete.clearInput();
        }

        this.onModelTouched();
        this.onModelChange(this.value);
    }

    isDuplicate(chip: T): boolean {
        return !!this.value.find(currentChip => {

            if (this.duplicateCheckField) {
                return chip[this.duplicateCheckField] === currentChip[this.duplicateCheckField];
            }

            if (this.field) {
                return chip[this.field] === currentChip[this.field];
            }

            return chip === currentChip;
        });
    }

    isChipValid(chip: T): boolean {
        let isChipValid: boolean = true;

        if (this.chipValidations && this.chipValidations.length > 0) {
            // have to go over each option to make sure that all errors show up
            for (let i = 0; i < this.chipValidations.length; i++) {
                const validation: InputValidationModel = this.chipValidations[i];

                // practically should always be of type string, but we aren't garunteeing via the type-system
                // that type T has a property of type string, indexed by 'this.field', ie: (typeof T[this.field]) === 'string'
                let str: any = chip;
                if (this.field) {
                    str = chip[this.field];
                }

                if (!validation.isValid(str)) {
                    // don't add duplicate error messages.
                    if (!this.chipValidationErrors.includes(validation.errorKey)) {
                        this.chipValidationErrors.push(validation.errorKey);
                    }

                    isChipValid = false;
                }
            }
        }

        return isChipValid;
    }

    autocompleteKeyUp(event: KeyboardEvent): void {
        if (!this.addOnKeys.find(key => key === event.key)) {
            return;
        }

        this.enterKeyUp.emit(this.searchString);
        this.autocomplete.clearInput();
        this.autocomplete.autocomplete.overlayVisible = false;
        event.preventDefault();
    }

    clearAllChips(allChipsRemoved: boolean): void {
        this.allChipsRemoved.emit(allChipsRemoved);
        requestAnimationFrame(() => this.autocomplete.autocomplete.inputEL.nativeElement.focus());
    }

}
